import { bopApi } from '@/lib/api/client';

// PRESERVE: plural paths for list/auto-assign/manual-assign, SINGULAR
// `parked-deposit` segment for revert/ignore — easy to get wrong.
const LIST_PATH = 'v1/parked-deposits';
const MANUAL_ASSIGN_PATH = 'v1/parked-deposits/manual-assign';
const REVERT_PATH = 'v1/parked-deposit/revert';
const IGNORE_PATH = 'v1/parked-deposit/ignore';

/**
 * Raw snake_case record from funds. Fields observed in the legacy UI (table +
 * detail dialog + an older revision that rendered account number / BIC).
 * Records may contain more fields; the detail dialog renders every non-null field.
 */
export interface ParkedDeposit {
  /** uuid — the "processParkedDepositId" in mutations */
  transaction_id: string;
  /** ISO datetime */
  parked_at: string;
  currency_code: string;
  amount: string;
  /** SNAKE_CASE enum-ish, e.g. 'NO_MATCHING_PAYOUT_ACCOUNT' */
  parked_reason: string;
  reference?: string;
  counterparty_name?: string;
  counterparty_iban?: string;
  counterparty_account_number?: string;
  counterparty_bic?: string;
  /** ISO datetime */
  bcb_completed_at?: string;
  /** ISO datetime */
  exchange_processed_at?: string;
  [additionalData: string]: unknown;
}

interface GetParkedDepositsResponse {
  parkedDeposits?: unknown;
  [envelopeField: string]: unknown;
}

export interface AutoAssignParkedDepositsResponse {
  /** Funds result object — displayed raw via JSON.stringify */
  results?: unknown;
  [envelopeField: string]: unknown;
}

export interface ManualAssignParkedDepositRequest {
  transactionId: string;
  payoutAccountId: string;
  /** Audit justification */
  comment: string;
}

export interface RevertParkedDepositRequest {
  /** PRESERVE: the UI concept "transactionId" is renamed on the wire */
  processParkedDepositId: string;
  /** Audit justification (legacy UI called this "auditJustification") */
  comment: string;
}

export interface IgnoreParkedDepositRequest {
  transactionId: string;
  /** Free text shown to no one afterwards — distinct from the audit justification */
  ignoringReason: string;
  /** Audit justification */
  comment: string;
}

interface FundsEnvelope {
  success?: unknown;
  error?: unknown;
  [envelopeField: string]: unknown;
}

/**
 * KNOW: bop-api returns 500 only when funds responds `success:false`
 * WITH an `error`; a `success:false` without an error still comes back as HTTP
 * 200 (audit record written and all). Inspect the 200 body and treat it as a
 * failure. Bodies are ambiguous funds envelopes, so parse from text.
 */
const assertFundsSuccess = (rawBody: string, action: string): void => {
  if (!rawBody) return;
  let parsed: unknown;
  try {
    parsed = JSON.parse(rawBody);
  } catch {
    return; // non-JSON 200 body — nothing to inspect
  }
  if (typeof parsed !== 'object' || parsed === null) return;
  const envelope = parsed as FundsEnvelope;
  if (envelope.success === false) {
    const message = typeof envelope.error === 'string' && envelope.error ? envelope.error : `Funds reported the ${action} failed`;
    throw new Error(message);
  }
};

/**
 * KNOW: listing IS the funds auto-cleanup lambda in dry-run mode, and
 * funds failures bubble through as HTTP 200 without a `parkedDeposits` array —
 * guard and surface that as a real query error instead of rendering nothing.
 */
export const getParkedDeposits = async (): Promise<ParkedDeposit[]> => {
  const body = await bopApi.get(LIST_PATH).json<GetParkedDepositsResponse>();
  if (!Array.isArray(body.parkedDeposits)) {
    throw new Error('Funds did not return a parked deposit list — the upstream lambda likely failed');
  }
  return body.parkedDeposits as ParkedDeposit[];
};

// PRESERVE: literal `dryRun: false` plus the `processParkedDepositIds`
// rename — the same lambda as the GET, switched into assign mode.
export const autoAssignParkedDeposits = (body: { processParkedDepositIds: string[]; comment: string }) =>
  bopApi.post(LIST_PATH, { json: { dryRun: false, ...body } }).json<AutoAssignParkedDepositsResponse>();

export const manualAssignParkedDeposit = async (body: ManualAssignParkedDepositRequest): Promise<void> => {
  const rawBody = await bopApi.post(MANUAL_ASSIGN_PATH, { json: body }).text();
  assertFundsSuccess(rawBody, 'manual assign');
};

export const revertParkedDeposit = async (body: RevertParkedDepositRequest): Promise<void> => {
  const rawBody = await bopApi.post(REVERT_PATH, { json: body }).text();
  assertFundsSuccess(rawBody, 'revert');
};

export const ignoreParkedDeposit = async (body: IgnoreParkedDepositRequest): Promise<void> => {
  const rawBody = await bopApi.post(IGNORE_PATH, { json: body }).text();
  assertFundsSuccess(rawBody, 'ignore');
};
